import { CATEGORY_ORDER } from './categoryPalette.js';

function stageSummary(meta, name) {
  return meta?.stage_metadata?.[name]?.summary || meta?.stages?.[name]?.summary || {};
}

/** @param {unknown[]} candidates */
function firstNumber(...candidates) {
  for (const v of candidates) {
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
  }
  return null;
}

/** @param {Array<{ category?: string }>} articles */
export function categorySpreadFromArticles(articles) {
  return (articles || []).reduce((acc, a) => {
    const cat = a?.category;
    if (!cat || cat === '—') return acc;
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, /** @type {Record<string, number>} */ ({}));
}

/** @param {Array<{ source?: { displayName?: string, domain?: string } }>} articles */
function topSourcesFromArticles(articles, limit = 4) {
  const sourceCounts = (articles || []).reduce((acc, a) => {
    const name = a?.source?.displayName || a?.source?.domain;
    if (!name) return acc;
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, /** @type {Record<string, number>} */ ({}));
  return Object.entries(sourceCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([name, n]) => ({ name, n }));
}

/**
 * Pipeline debrief numbers from an Edition (uses pipelineMeta when present).
 * @param {object | null | undefined} edition
 */
export function deriveBriefStats(edition) {
  if (!edition) return null;

  const articles = edition.articles || [];
  const meta = edition.pipelineMeta || {};
  const counts = meta?.counts || {};
  const ingestSummary = stageSummary(meta, 'stage_1_ingest');
  const filterSummary = stageSummary(meta, 'stage_1_5_filter');
  const dedupSummary = stageSummary(meta, 'stage_2_5_dedup');
  const classifySummary = stageSummary(meta, 'stage_2_classify');

  const reviewed = firstNumber(
    ingestSummary.raw_total,
    ingestSummary.unique_total,
    ingestSummary.total_articles,
    filterSummary.input_count,
    classifySummary.input_count
  );

  const filteredOut = firstNumber(filterSummary.rejected_count);
  const storyDupesMerged = firstNumber(dedupSummary.removed_count);

  const inBrief = articles.length;

  let categorySpread = categorySpreadFromArticles(articles);
  if (Object.keys(categorySpread).length === 0 && counts.by_category) {
    categorySpread = { ...counts.by_category };
  }

  const topSources = topSourcesFromArticles(articles);

  return {
    reviewed,
    filteredOut,
    storyDupesMerged,
    inBrief,
    categorySpread,
    topSources,
    hasPipelineMeta: Boolean(meta && Object.keys(meta).length > 0),
  };
}

/** Safe subset for public API (no raw pipelineMeta). */
export function toPublicBriefStats(edition) {
  const stats = deriveBriefStats(edition);
  if (!stats) return null;
  return {
    reviewed: stats.reviewed,
    filteredOut: stats.filteredOut,
    storyDupesMerged: stats.storyDupesMerged,
    inBrief: stats.inBrief,
    categorySpread: stats.categorySpread,
    topSources: stats.topSources.slice(0, 3),
    hasPipelineMeta: stats.hasPipelineMeta,
    editionStatus: edition.status || null,
  };
}

/**
 * Stats for the homepage / preview model — always includes category spread from
 * the articles we actually render, with pipeline totals when available.
 * @param {object | null | undefined} edition
 * @param {Array<{ category?: string, source?: object }>} [previewArticles]
 */
export function resolveModelBriefStats(edition, previewArticles = []) {
  const server = edition?.briefStats;
  const fromPipeline = edition?.pipelineMeta ? deriveBriefStats(edition) : null;
  const articles = previewArticles?.length ? previewArticles : edition?.articles || [];

  const spreadFromPreview = categorySpreadFromArticles(articles);
  const inBrief = articles.length;

  const categorySpread =
    Object.keys(spreadFromPreview).length > 0
      ? spreadFromPreview
      : { ...(server?.categorySpread || fromPipeline?.categorySpread || {}) };

  const topSources =
    topSourcesFromArticles(articles, 3).length > 0
      ? topSourcesFromArticles(articles, 3)
      : server?.topSources || fromPipeline?.topSources?.slice(0, 3) || [];

  return {
    reviewed: server?.reviewed ?? fromPipeline?.reviewed ?? null,
    filteredOut: server?.filteredOut ?? fromPipeline?.filteredOut ?? null,
    storyDupesMerged: server?.storyDupesMerged ?? fromPipeline?.storyDupesMerged ?? null,
    inBrief: inBrief || server?.inBrief || fromPipeline?.inBrief || 0,
    categorySpread,
    topSources,
    hasPipelineMeta: Boolean(server?.hasPipelineMeta ?? fromPipeline?.hasPipelineMeta),
    editionStatus: edition?.status || server?.editionStatus || null,
  };
}

/** @param {number | null | undefined} n */
export function formatBriefStat(n) {
  if (typeof n !== 'number' || Number.isNaN(n)) return '—';
  return n.toLocaleString('en-US');
}

/**
 * @param {Record<string, number>} spread
 * @param {string[]} [order]
 */
export function categorySpreadEntries(spread, order = CATEGORY_ORDER) {
  const safe = spread || {};
  const known = order.filter((c) => (safe[c] || 0) > 0).map((c) => [c, safe[c]]);
  const extra = Object.entries(safe).filter(([c, n]) => n > 0 && !order.includes(c));
  return [...known, ...extra];
}
